import { Button, Modal, notify } from '@affine/component';
import { getStoreManager } from '@affine/core/blocksuite/manager/store';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  type DialogComponentProps,
  type WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { IntegrationService } from '@affine/core/modules/integration';
import { splitMarkdownTitle } from '@affine/core/modules/integration/utils/markdown-title';
import { WorkbenchService } from '@affine/core/modules/workbench';
import {
  getAFFiNEWorkspaceSchema,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import { MarkdownTransformer } from '@blocksuite/affine/widgets/linked-doc';
import { useLiveData, useService } from '@toeverything/infra';
import { cssVarV2 } from '@toeverything/theme/v2';
import { useCallback, useState } from 'react';

import {
  type GitHubFileSelection,
  RepoBrowser,
} from '../setting/workspace-setting/integration/github/repo-browser';
import * as styles from './styles.css';

type Status = 'browsing' | 'importing' | 'success' | 'error';

export const GitHubImportDialog = ({
  close,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['github-import']>) => {
  const github = useService(IntegrationService).github;
  const settings = useLiveData(github.settings$);
  const workspace = useService(WorkspaceService).workspace;
  const docCollection = workspace.docCollection;
  const workbench = useService(WorkbenchService).workbench;

  const [status, setStatus] = useState<Status>('browsing');
  const [importError, setImportError] = useState<string | null>(null);
  const [importedDocs, setImportedDocs] = useState<string[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleImport = useAsyncCallback(
    async (selection: GitHubFileSelection) => {
      if (!settings?.accessToken || selection.files.length === 0) return;

      setStatus('importing');
      setImportError(null);
      setProgress({ current: 0, total: selection.files.length });

      const docIds: string[] = [];
      let failedCount = 0;

      for (let i = 0; i < selection.files.length; i++) {
        const file = selection.files[i];
        setProgress({ current: i + 1, total: selection.files.length });

        try {
          // Fetch file content from GitHub
          const { content, sha } = await github.getFileContent(
            selection.owner,
            selection.repo,
            file.path,
            selection.branch
          );

          // The H1 becomes the doc title (falling back to the filename) and
          // is kept out of the body
          const { title: titleFromH1, body: markdownWithoutTitle } =
            splitMarkdownTitle(content);
          const fileName =
            titleFromH1 || file.name.replace('.md', '') || 'Untitled';

          // Import markdown to doc
          const docId = await MarkdownTransformer.importMarkdownToDoc({
            collection: docCollection,
            schema: getAFFiNEWorkspaceSchema(),
            markdown: markdownWithoutTitle,
            fileName,
            extensions: getStoreManager().config.init().value.get('store'),
          });

          if (docId) {
            docIds.push(docId);

            // Store the integration reference for syncing
            await github.setDocIntegrationRef(
              docId,
              selection.owner,
              selection.repo,
              file.path,
              selection.branch,
              sha
            );
          }
        } catch (error) {
          console.error(`Failed to import ${file.path}:`, error);
          failedCount++;
        }
      }

      setImportedDocs(docIds);

      if (docIds.length > 0) {
        setStatus('success');
        const message =
          failedCount > 0
            ? `Imported ${docIds.length} file${docIds.length !== 1 ? 's' : ''} (${failedCount} failed)`
            : `Imported ${docIds.length} file${docIds.length !== 1 ? 's' : ''} from GitHub`;
        notify.success({
          title: 'Import successful',
          message,
        });
      } else {
        setStatus('error');
        setImportError('Failed to import any files. Please try again.');
      }
    },
    [docCollection, github, settings?.accessToken]
  );

  const handleCancel = useCallback(() => {
    close(undefined);
  }, [close]);

  const handleComplete = useCallback(() => {
    // Open the first imported doc
    if (importedDocs.length > 0) {
      workbench.openDoc(importedDocs[0]);
    }
    close({ docIds: importedDocs });
  }, [close, importedDocs, workbench]);

  const handleRetry = useCallback(() => {
    setStatus('browsing');
    setImportError(null);
    setProgress({ current: 0, total: 0 });
  }, []);

  const renderContent = () => {
    switch (status) {
      case 'browsing':
        return <RepoBrowser onImport={handleImport} onCancel={handleCancel} />;
      case 'importing':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.statusTitle}>Importing from GitHub...</div>
            <p className={styles.statusMessage}>
              Importing file {progress.current} of {progress.total}...
            </p>
          </div>
        );
      case 'success':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.statusTitle}>Import Complete!</div>
            <p className={styles.statusMessage}>
              Successfully imported {importedDocs.length} file
              {importedDocs.length !== 1 ? 's' : ''} from GitHub.
            </p>
            <div className={styles.buttonContainer}>
              <Button variant="primary" onClick={handleComplete}>
                Done
              </Button>
            </div>
          </div>
        );
      case 'error':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.statusTitle}>Import Failed</div>
            <p className={styles.statusMessage}>
              {importError || 'An error occurred while importing files.'}
            </p>
            <div className={styles.buttonContainer}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Try Again
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open: boolean) => {
        if (!open) {
          close(importedDocs.length > 0 ? { docIds: importedDocs } : undefined);
        }
      }}
      width={640}
      contentOptions={{
        style: {
          maxHeight: '85vh',
          maxWidth: '90vw',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: cssVarV2('layer/background/primary'),
        },
      }}
      withoutCloseButton={status === 'importing'}
      persistent={status === 'importing'}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Import from GitHub</h2>
        </div>
        {renderContent()}
      </div>
    </Modal>
  );
};
